import { AxiosInstance, AxiosResponse } from 'axios';
import { EndpointOptions } from '@/types/endpoint.options';
import { withSuppressedErrorToast } from '@/client/get-axios-instance';
import { extractSelectionLimitWarning, triggerBlobDownload } from '@/repositories/csv-download';
import { createRepositoryApiErrorFromResponse, normalizeRepositoryError } from '@/repositories/repository-api-error';
import {
    parseUnsupportedMarketplaceAxiosBlobResponse,
    UnsupportedMarketplaceRouteError,
} from '@/repositories/unsupported-marketplace-error';

export type GroupMergePayload = {
    groupIds: string[];
    name: string;
};

export type GroupMergeResponse = {
    targetGroupId: string;
};

export type GroupRenamePayload = {
    name: string;
};

export type GroupRenameResponse = {
    id: string;
    name: string;
};

export type GroupExtractResponse = {
    movedCount: number;
    sourceGroupDeleted: boolean;
};

export type GroupCsvDownloadResult = {
    limitWarning: string | null;
};

const withEndpointOptions = (options?: EndpointOptions) => ({
    headers: { Authorization: options?.authToken },
    signal: options?.signal,
});

const withHandledEndpointOptions = (options?: EndpointOptions) => {
    return withSuppressedErrorToast(withEndpointOptions(options));
};

type CreateGroupMutationRepositoryHandlersArgs = {
    axios: AxiosInstance;
    csvFileName: string;
    csvDownloadFallbackCode: string;
    csvDownloadFallbackMessage: string;
};

export type GroupMutationRepositoryHandlers<
    TGroupDetailsResponse,
    TRenameResponse extends GroupRenameResponse,
    TExtractPayload extends Record<string, unknown>,
    TExtractResponse extends GroupExtractResponse,
    TCsvQuery extends Record<string, unknown>,
> = {
    mergeGroups: (data: GroupMergePayload, options?: EndpointOptions) => Promise<AxiosResponse<GroupMergeResponse>>;
    getGroup: (groupId: string, options?: EndpointOptions) => Promise<AxiosResponse<TGroupDetailsResponse>>;
    renameGroup: (
        groupId: string,
        data: GroupRenamePayload,
        options?: EndpointOptions,
    ) => Promise<AxiosResponse<TRenameResponse>>;
    extractFromGroup: (
        groupId: string,
        data: TExtractPayload,
        options?: EndpointOptions,
    ) => Promise<AxiosResponse<TExtractResponse>>;
    downloadCsv: (query: TCsvQuery, options?: EndpointOptions) => Promise<GroupCsvDownloadResult>;
};

export const createGroupMutationRepositoryHandlers = <
    TGroupDetailsResponse,
    TRenameResponse extends GroupRenameResponse,
    TExtractPayload extends Record<string, unknown>,
    TExtractResponse extends GroupExtractResponse,
    TCsvQuery extends Record<string, unknown>,
>({
    axios,
    csvFileName,
    csvDownloadFallbackCode,
    csvDownloadFallbackMessage,
}: CreateGroupMutationRepositoryHandlersArgs): GroupMutationRepositoryHandlers<
    TGroupDetailsResponse,
    TRenameResponse,
    TExtractPayload,
    TExtractResponse,
    TCsvQuery
> => ({
    mergeGroups(data, options) {
        return axios.post<GroupMergeResponse>('/groups/merge', data, withHandledEndpointOptions(options));
    },

    getGroup(groupId, options) {
        return axios.get<TGroupDetailsResponse>(`/${groupId}`, withEndpointOptions(options));
    },

    renameGroup(groupId, data, options) {
        return axios.patch<TRenameResponse>(`/${groupId}`, data, withHandledEndpointOptions(options));
    },

    extractFromGroup(groupId, data, options) {
        return axios.post<TExtractResponse>(`/${groupId}/extract`, data, withHandledEndpointOptions(options));
    },

    async downloadCsv(query, options) {
        try {
            const response = await axios.post<Blob>('/download-csv', query, {
                ...withHandledEndpointOptions(options),
                responseType: 'blob',
                validateStatus: (status) => status < 500,
            });

            const unsupportedPayload = await parseUnsupportedMarketplaceAxiosBlobResponse({
                status: response.status,
                headers: response.headers,
                data: response.data,
            });
            if (unsupportedPayload) {
                throw new UnsupportedMarketplaceRouteError(unsupportedPayload);
            }

            if (response.status >= 400) {
                throw await createRepositoryApiErrorFromResponse({
                    status: response.status,
                    payload: response.data,
                    headers: response.headers,
                    fallbackMessage: `${csvDownloadFallbackMessage} (HTTP ${response.status})`,
                    fallbackCode: csvDownloadFallbackCode,
                    fallbackCategory: 'transport',
                });
            }

            const headers = response.headers as Record<string, unknown>;
            const limitWarning = extractSelectionLimitWarning(headers);

            triggerBlobDownload(response.data, headers, csvFileName);

            return { limitWarning };
        } catch (error) {
            throw await normalizeRepositoryError({
                error,
                fallbackMessage: csvDownloadFallbackMessage,
                fallbackCode: csvDownloadFallbackCode,
                fallbackCategory: 'transport',
            });
        }
    },
});
